import MainNavigationRail from './MainNavigationRail';
import { View, StyleSheet, Platform, Pressable } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import React, { useCallback, useState, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import SidebarHeader from './SidebarHeader';
import { useTheme } from '@/context/ThemeContext';
import { useRouter } from 'expo-router';
import { useAuthStore, useModalStore, useChatStore, useUIStore } from '@/store';
import useResponsiveness from '@/hooks/useResponsiveness';
import SidebarListItemLayout from './SidebarListItemLayout';
import PropTypes from 'prop-types';
import { Link } from 'expo-router';
import SidebarSavedAgentsSection from './SidebarSavedAgentsSection';
import SidebarSavedModelsSection from './SidebarSavedModelsSection';
import SidebarChatsSection from './SidebarChatsSection';
import SidebarCollectionsSection from './SidebarCollectionsSection';
import SidebarChecklist from './SidebarChecklist';
import SidebarGetAppDropdown from './SidebarGetAppDropdown';
import Text from '@/components/Typography/Text';
import Button from '@/components/Buttons/Button';
import {
  clampSidebarWidth,
} from '@/constants/config';
import BannerPlacement from '@/components/Banner/BannerPlacement';
import { useTooltipAnchor } from '@/hooks/useTooltipAnchor';
import { openNewChatComposer } from '@/lib/openHomeComposer';
// Create styles outside component
const createStyles = (colors, safeAreaTop, safeAreaBottom, isXS) => StyleSheet.create({
  container: {
    zIndex: 9999,
    height: '100%',
    position: 'relative',
    display: 'flex',
    flexDirection: 'row',
    borderRightWidth: 1,
    borderRightColor: colors.border_color_secondary,
    backgroundColor: colors.solid_color_block,
    paddingTop: safeAreaTop,
    paddingBottom: safeAreaBottom,
  },
  listContainer: {
    flex: 1,
    paddingLeft: 8,
    paddingRight: 8,
    paddingBottom: 20,
    // The SidebarHeader already reserves space for itself,
    // so this margin is just visual breathing space between the header and
    // the first list item.
    marginTop: 8,
    ...(Platform.OS === 'web' ? { overflow: 'auto', scrollbarWidth: 'thin' } : { overflow: 'hidden' }),
    backgroundColor: colors.solid_color_block,
  },
  resizeHandle: {
    position: 'absolute',
    top: 0,
    // Keep the resize target entirely outside the scrolling sidebar. A
    // straddling hit target covers Firefox's thin scrollbar on Linux.
    right: -8,
    width: 8,
    height: '100%',
    cursor: 'ew-resize',
    touchAction: 'none',
    zIndex: 2,
  },
  resizeHandleHovered: {
    backgroundColor: 'rgba(0,0,0,0.1)',
  },
  resizeHandleActive: {
    backgroundColor: 'rgba(0,0,0,0.2)',
  },
});

const lowerSectionStyles = (colors) => StyleSheet.create({
  lowerSection: {
    borderTopWidth: 1,
    borderTopColor: colors.border_color_secondary,
  },
});

const proSectionStyles = (colors) => StyleSheet.create({
  proSection: {
    paddingHorizontal: 15,
    paddingBottom: 10,
    paddingTop: 10,
    marginBottom: Platform.OS === 'web' ? 0 : 15,
  },
  proTitle: {
    marginRight: 4,
    marginBottom: 10,
  },
  proDescription: {
    marginBottom: 14,
    marginTop: 0,
  },
});

const helpLinksStyles = StyleSheet.create({
  // Rendered inline inside the logged-out nav list (which already provides the
  // 8px horizontal gutter), so no extra horizontal padding here — just a small
  // top gap to separate it from "Create Music".
  container: {
    paddingTop: 6,
    paddingBottom: 10,
  },
});

// Logged-out footer links shown inline beneath "Create Music" in the nav list.
const SidebarHelpLinks = () => {
  const { t } = useTranslation();

  return (
    <View style={helpLinksStyles.container}>
      <SidebarGetAppDropdown />
      <Link href="/docs" asChild withAnchor={true}>
        <SidebarListItemLayout
          id="help-documentation"
          title={t('sidebar.apiDocumentation')}
          icon="book-open"
          noEllipsis
        />
      </Link>
    </View>
  );
};

const SidebarAuthUpsellSection = ({ isXS }) => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const styles = useMemo(() => proSectionStyles(colors), [colors]);
  const { openModal } = useModalStore(state => state.actions);
  const router = useRouter();
  const searchQuery = useChatStore(state => state.searchQuery);

  const handleSignupClick = useCallback(() => {
    if (Platform.OS === 'web' && !isXS) {
      openModal('SignupModal');
    } else {
      router.push('/signup');
    }
  }, [openModal, router, isXS]);

  const handleLoginClick = useCallback(() => {
    if (Platform.OS === 'web' && !isXS) {
      openModal('LoginModal');
    } else {
      router.push('/login');
    }
  }, [openModal, router, isXS]);

  if (!!searchQuery && isXS) return null;

  return (
    <View style={styles.proSection}>
      <Text size="m" weight="bold" style={styles.proTitle}>
        {t('sidebar.upsellTitle')}
      </Text>
      <Text size="s" color="secondary" style={styles.proDescription}>
        {t('sidebar.upsellBody')}
      </Text>
      <View style={{ flexDirection: 'column', gap: 8 }}>
        <Button
          text={t('sidebar.logIn')}
          variant="secondary"
          size="medium"
          onPress={handleLoginClick}
        />
        <Button
          text={t('common.signUp')}
          variant="plus"
          size="medium"
          onPress={handleSignupClick}
        />
      </View>
    </View>
  );
};

const SidebarLowerSection = ({
  hasUser,
  isXS,
  isSearching,
}) => {
  const { colors } = useTheme();
  const styles = useMemo(() => lowerSectionStyles(colors), [colors]);

  return (
    <View style={styles.lowerSection}>
      <BannerPlacement placement="sidebar" isCompact style={{ paddingHorizontal: 10, paddingTop: 10 }} />
      {!hasUser && <SidebarAuthUpsellSection isXS={isXS} />}
    </View>
  );
};

export function AppSidebar({ width, onResize, onToggle, pwaSafeAreaTop = 0 }) {
  const { t } = useTranslation();
  const insets = useSafeAreaInsets();
  const [isResizing, setIsResizing] = useState(false);
  const resizeCleanupRef = useRef(null);
  useEffect(() => () => resizeCleanupRef.current?.(), []);
  const { colors } = useTheme();
  const router = useRouter();
  const { isXS } = useResponsiveness();
  const newChatRef = useTooltipAnchor('new-chat');
  const searchQuery = useChatStore(state => state.searchQuery);
  const isSearching = !!searchQuery;

  const userId = useAuthStore(state => state.user?.id);
  const hasUser = Boolean(userId);
  // Memoize styles - apply PWA safe area for web when provided
  const safeAreaTop = Platform.select({ ios: insets.top, android: insets.top, default: pwaSafeAreaTop });
  const safeAreaBottom = Platform.select({ ios: insets.bottom, android: insets.bottom, default: 0 });
  const styles = useMemo(() => createStyles(colors, safeAreaTop, safeAreaBottom, isXS), [colors, safeAreaTop, safeAreaBottom, isXS]);

  const handleNewChatPress = useCallback(() => {
    openNewChatComposer(router);
    if (isXS) useUIStore.getState().setSidebarVisible(false);
  }, [router, isXS]);

  const handlePointerDown = useCallback((e) => {
    if (e.button !== 0 || e.isPrimary === false || !onResize) return;
    e.preventDefault();
    resizeCleanupRef.current?.();
    const handle = e.currentTarget;
    const pointerId = e.pointerId;
    const startX = e.clientX;
    const startWidth = width;
    let latestWidth = width;
    let rafId;
    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;
    document.body.style.cursor = 'ew-resize';
    document.body.style.userSelect = 'none';
    handle.setPointerCapture?.(pointerId);
    setIsResizing(true);
    onResize(width, true);

    // Anchor to the grabbed position so the outside hit target cannot cause a jump.
    const widthFromEvent = event => clampSidebarWidth(startWidth + event.clientX - startX, window.innerWidth);
    const handlePointerMove = (moveEvent) => {
      if (moveEvent.pointerId !== pointerId) return;
      latestWidth = widthFromEvent(moveEvent);
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        onResize(latestWidth, true);
      });
    };

    const finishResize = (event) => {
      if (event.pointerId != null && event.pointerId !== pointerId) return;
      if (event.type === 'pointerup') latestWidth = widthFromEvent(event);
      resizeCleanupRef.current?.();
      setIsResizing(false);
      onResize(latestWidth, false);
    };

    resizeCleanupRef.current = () => {
      cancelAnimationFrame(rafId);
      document.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('pointerup', finishResize);
      document.removeEventListener('pointercancel', finishResize);
      handle.removeEventListener('lostpointercapture', finishResize);
      window.removeEventListener('blur', finishResize);
      if (handle.hasPointerCapture?.(pointerId)) handle.releasePointerCapture(pointerId);
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
      resizeCleanupRef.current = null;
    };
    document.addEventListener('pointermove', handlePointerMove);
    document.addEventListener('pointerup', finishResize);
    document.addEventListener('pointercancel', finishResize);
    handle.addEventListener('lostpointercapture', finishResize);
    window.addEventListener('blur', finishResize);
  }, [onResize, width]);

  // Memoize resize handle
  const resizeHandle = useMemo(() => {
    if (Platform.OS !== 'web' || isXS || !onResize) return null;
    
    return (
      <Pressable 
        accessibilityLabel="Resize navigation pane"
        style={({ hovered }) => [
          styles.resizeHandle,
          hovered && styles.resizeHandleHovered,
          isResizing && styles.resizeHandleActive
        ]}
        onPointerDown={handlePointerDown}
      />
    );
  }, [isXS, styles, isResizing, handlePointerDown, onResize]);

  return (
    <View style={[styles.container, { width }]}>
      <MainNavigationRail onNavigate={() => { if (isXS) useUIStore.getState().setSidebarVisible(false); }} />
      <View style={{ flex: 1, minWidth: 0, minHeight: 0 }}>
        <SidebarHeader withSearch={hasUser} onToggle={onToggle} />
        <View style={styles.listContainer}>
          {!isSearching && <View ref={newChatRef}><SidebarListItemLayout
            id="new-chat" title={t('nav.routes.chat.newChat')} icon="pen-to-square"
            onPress={handleNewChatPress} noEllipsis
          /></View>}
          {!isSearching && hasUser && <SidebarChecklist navigation={{
            closeDrawer: () => { if (isXS) useUIStore.getState().setSidebarVisible(false); },
          }} />}
          {!isSearching && hasUser && <SidebarChatsSection section="pinned" />}
          {!isSearching && hasUser && <SidebarCollectionsSection />}
          {!isSearching && hasUser && <SidebarSavedAgentsSection />}
          {!isSearching && hasUser && <SidebarSavedModelsSection />}
          {hasUser && <SidebarChatsSection />}
          {!hasUser && <SidebarHelpLinks />}
        </View>
        <SidebarLowerSection hasUser={hasUser} isXS={isXS} isSearching={isSearching} />
      </View>

      {resizeHandle}
    </View>
  );
}

AppSidebar.propTypes = {
  width: PropTypes.number,
  onResize: PropTypes.func,
  onToggle: PropTypes.func,
  pwaSafeAreaTop: PropTypes.number,
};

AppSidebar.whyDidYouRender = {
  logOnDifferentValues: true,
  customName: 'AppSidebar',
  logOwnerReasons: true
};

export default React.memo(AppSidebar);
